import ChatApi from '@/api/chat'
import Socket from '@/socket'
import { throttle as _throttle, omit as _omit } from 'lodash-es'
import Util from '@/util'
import sentry from '@/3rd-parties/sentry'
import GaCategories from '@/constants/GaCategories'
import * as localStorageExpiry from '@/utils/local.storage.expiry'

const SET_SENDING_MESSAGE = 'SET_SENDING_MESSAGE'
const SET_SUBSCRIBING_ROOM = 'SET_SUBSCRIBING_ROOM'
const SET_ACCESS_CODE = 'SET_ACCESS_CODE'
const SET_PRESENTATION_ID = 'SET_PRESENTATION_ID'
const SET_AUDIENCE_ID = 'SET_AUDIENCE_ID'
const SET_AUDIENCE_NAME = 'SET_AUDIENCE_NAME'
const ADD_MESSAGE = 'ADD_MESSAGE'
const SET_MESSAGES = 'SET_MESSAGES'
const TOGGLE_CHAT_BOX = 'TOGGLE_CHAT_BOX'
const TOGGLE_FOCUS_CHAT_INPUT = 'TOGGLE_FOCUS_CHAT_INPUT'
const SHOW_CHAT_NOTIFICATION = 'SHOW_CHAT_NOTIFICATION'
const MAX_MESSAGES_IN_CHAT = 200
const MESSAGE_EXPIRY = 30 * 24 * 60 * 60 * 1000

const Events = {
  NEW_MESSAGE: 'chat:new-message',
  CLEAR_MESSAGES: 'chat:clear-messages',
}

const getStorage = () => {
  return window.localStorage
}

const getStorageKey = (accessCode) => {
  return `chat-messages-${accessCode}`
}

export const getMessagesLocally = (accessCode) => {
  let messages = localStorageExpiry.getWithExpiry(getStorageKey(accessCode))
  if (!messages) {
    messages = '[]'
    localStorageExpiry.setWithExpiry(getStorageKey(accessCode), messages, MESSAGE_EXPIRY)
  }
  return JSON.parse(messages)
}

const saveMessagesLocally = ({ accessCode, messages, isThrottled }) => {
  // save messages every 10 seconds, because writing to the
  // localStorage is synchronous, and it would block the application
  // if there're lots of incoming messages
  if (isThrottled) {
    return _throttle(() => {
      localStorageExpiry.setWithExpiry(getStorageKey(accessCode), JSON.stringify(messages), MESSAGE_EXPIRY)
    }, 10e3)
  }
  return () => {
    if (getStorage()) {
      localStorageExpiry.setWithExpiry(getStorageKey(accessCode), JSON.stringify(messages), MESSAGE_EXPIRY)
    }
  }
}

const deleteMessagesLocally = ({ uniqueAccessCode }) => {
  getStorage().removeItem(getStorageKey(uniqueAccessCode))
}

const convertMessage = (data) => {
  if (!data) return {}
  data.received = true
  return data
}

const getMessageIndex = (messages, msg) => {
  if (!messages || !msg) return -1
  return messages.findIndex((m) => m.timestamp === msg.timestamp)
}

const addIsSelfToMsg = (audienceId, msg) => {
  return {
    ...msg,
    isSelf: audienceId === msg.userId,
  }
}

const getChatOrigin = (rootState) => {
  if (rootState.slide.type === 'pickAnswer' && rootState.slide.quizStatus === 1) {
    return 'Lobby'
  }
  if (rootState.slide.type === 'qrcode') {
    return 'QR code'
  }
  if (rootState.slide.type === 'leaderboard') {
    return 'Leaderboard'
  }
  return 'Quiz'
}

const _state = {
  isSendingMessage: false,
  accessCode: null,
  presentationId: null,
  audienceId: null,
  audienceName: null,
  newMessageHandler: null,
  isSubscribingChatRoom: false,
  messages: [],
  isOpenChatBox: false,
  isFocusingChatInput: false,
  hasChatNotification: false,
}

const getters = {
  isSendingMessage(state) {
    return state.isSendingMessage
  },
  isSubscribingChatRoom(state) {
    return state.isSubscribingChatRoom
  },
  chatMessages(state, __, ___, rootGetters) {
    const isShownEmojiAvatar = rootGetters['presentationSettings/isShownEmojiAvatar']
    return state.messages.map((message) => {
      return isShownEmojiAvatar ? message : _omit(message, 'avatar')
    })
  },
  isOpenChatBox(state) {
    return state.isOpenChatBox
  },
  isFocusingChatInput(state) {
    return state.isFocusingChatInput
  },
  hasChatNotification(state) {
    return state.hasChatNotification
  },
}

const actions = {
  subscribeChatRoomAction: async (
    { commit, dispatch, state },
    {
      presentation,
      newMessageHandler,
      audienceId,
      audienceName,
    },
  ) => {
    if (presentation && presentation.uniqueAccessCode) {
      commit(SET_SUBSCRIBING_ROOM, true)
      commit(SET_ACCESS_CODE, presentation.uniqueAccessCode)
      commit(SET_PRESENTATION_ID, presentation.id)
      commit(SET_AUDIENCE_ID, audienceId)
      commit(SET_AUDIENCE_NAME, audienceName)
      commit(SET_MESSAGES, getMessagesLocally(presentation.uniqueAccessCode))
      Socket.getSocket().on(Events.NEW_MESSAGE, (data) => {
        const messageObj = convertMessage(data)
        if (newMessageHandler) newMessageHandler(messageObj)
        commit(ADD_MESSAGE, messageObj)
      })
      Socket.getSocket().on(Events.CLEAR_MESSAGES, () => {
        dispatch('clearChatMessagesAction', {
          uniqueAccessCode: state.accessCode,
        })
      })
    }
  },

  unsubscribeChatRoomAction: async ({ commit }, { presentation }) => {
    if (presentation && presentation.uniqueAccessCode) {
      commit(SET_SUBSCRIBING_ROOM, false)
      Socket.getSocket().off(Events.NEW_MESSAGE)
      Socket.getSocket().off(Events.CLEAR_MESSAGES)
    }
  },

  sendMessageAction: async ({ commit, state, rootState }, { payload, onSuccess, onFailure }) => {
    commit(SET_SENDING_MESSAGE, true)
    const chatOrigin = getChatOrigin(rootState)

    try {
      commit(ADD_MESSAGE, payload)
      const msg = await ChatApi.sendMessage(payload)
      commit(SET_SENDING_MESSAGE, false)
      Util.mixpanelTrack(
        GaCategories.AUDIENCE,
        'Chat > Audience sends a message successfully',
        chatOrigin,
      )
      if (onSuccess) {
        onSuccess(msg)
      }
    } catch (error) {
      sentry.captureException(error)
      commit(SET_SENDING_MESSAGE, false)
      commit(ADD_MESSAGE, { ...payload, received: false })
      if (onFailure) {
        Util.mixpanelTrack(
          GaCategories.AUDIENCE,
          `Chat > ${chatOrigin} > Audience sends a message failed`,
          `${state.presentationId} > Chat > ${state.audienceName} > ${error}`,
        )
        onFailure(error)
      }
    }
  },

  clearChatMessagesAction: async ({ commit }, { uniqueAccessCode }) => {
    commit(SET_MESSAGES, [])
    deleteMessagesLocally({ uniqueAccessCode })
  },

  toggleChatBoxAction: async ({ commit }, openState) => {
    if (openState) {
      commit(TOGGLE_CHAT_BOX, openState.isOpen)
    } else {
      commit(TOGGLE_CHAT_BOX)
    }
  },

  toggleFocusChatInputAction: async ({ commit }, focusState) => {
    if (focusState) {
      commit(TOGGLE_FOCUS_CHAT_INPUT, focusState.isFocus)
    } else {
      commit(TOGGLE_FOCUS_CHAT_INPUT)
    }
  },

  showChatNotificationAction: async ({ commit }, showState) => {
    commit(SHOW_CHAT_NOTIFICATION, showState.isShowing)
  },
}

const mutations = {
  [SET_SENDING_MESSAGE](state, isSendingMessage) {
    state.isSendingMessage = isSendingMessage
  },
  [SET_SUBSCRIBING_ROOM](state, isSubscribingChatRoom) {
    state.isSubscribingChatRoom = isSubscribingChatRoom
  },
  [SET_ACCESS_CODE](state, accessCode) {
    state.accessCode = accessCode
  },
  [SET_PRESENTATION_ID](state, id) {
    state.presentationId = id
  },
  [SET_AUDIENCE_ID](state, audienceId) {
    state.audienceId = audienceId
  },
  [SET_AUDIENCE_NAME](state, audienceName) {
    state.audienceName = audienceName
  },
  [ADD_MESSAGE](state, newMessage) {
    const index = getMessageIndex(state.messages, newMessage)
    const msgToAdd = addIsSelfToMsg(state.audienceId, newMessage)
    if (index === -1) {
      state.messages = [
        ...state.messages,
        msgToAdd,
      ]

      if (state.messages.length > MAX_MESSAGES_IN_CHAT) {
        // Is it okay to mutate?
        state.messages.splice(0, 1)
      }

      const saveMessageThrottled = saveMessagesLocally({
        accessCode: state.accessCode,
        messages: state.messages,
        throttled: true,
      })
      saveMessageThrottled()
    } else {
      const oldMsgs = [...state.messages]
      oldMsgs[index] = msgToAdd
      state.messages = oldMsgs
      const saveMessage = saveMessagesLocally({
        accessCode: state.accessCode,
        messages: state.messages,
        throttled: false,
      })
      saveMessage()
    }
  },
  [SET_MESSAGES](state, messages) {
    state.messages = messages
  },
  [TOGGLE_CHAT_BOX](state, openState) {
    if (typeof openState === 'boolean') {
      state.isOpenChatBox = openState
    } else {
      state.isOpenChatBox = !state.isOpenChatBox
    }
  },
  [TOGGLE_FOCUS_CHAT_INPUT](state, focusState) {
    if (typeof focusState === 'boolean') {
      state.isFocusingChatInput = focusState
    } else {
      state.isFocusingChatInput = !state.isFocusingChatInput
    }
  },
  [SHOW_CHAT_NOTIFICATION](state, showState) {
    state.hasChatNotification = showState
  },
}

export default {
  namespaced: true,
  state: _state,
  getters,
  actions,
  mutations,
}
